import { roundMoney } from '../../../utils/money';
import type { CompanyBudgetWorkspaceDto, CompanyBudgetYearMonthInput } from './companyBudgetApi';
import type { CompanyCostGroup } from '../../companyCosts/costGroups';

// Pure model of the year plan editor („Plan roku”, decision
// 2026-10-29-grupy-kosztow-i-budzet): a grid of rows × 12 months seeded from
// the saved budget, quick entry helpers and the save payload of the months
// that changed. The saved plan's derived figures (EBITDA, computed profit)
// come from the backend; the editor previews them for unsaved values with
// the same rules (`planPreview`).

export const MAX_PLAN_AMOUNT = 1_000_000_000;
// Mirror of backend incomeStatementService (INCOME_TAX_RATE, yearToDateTaxStep),
// used only for the preview of an unsaved plan; listed among the mirrors in
// utils/AGENTS.md, the fixture in budgetPlan.test.ts is the backend's.
export const PLAN_INCOME_TAX_RATE = 0.09;

export type PlanRowKey = 'revenue' | 'margin' | 'ungrouped' | 'profit' | `group:${string}`;
export type PlanValues = (number | null)[];
export type PlanGrid = Record<string, PlanValues>;

export interface PlanRow {
  key: PlanRowKey;
  kind: 'revenue' | 'margin' | 'group' | 'ungrouped' | 'profit';
  /** Group rows only. */
  group?: CompanyCostGroup;
  /** Margin and profit plans may be negative. */
  signed: boolean;
}

export const groupRowKey = (groupId: string): PlanRowKey => `group:${groupId}`;

const emptyRow = (): PlanValues => Array.from({ length: 12 }, () => null);
const monthKey = (year: number, index: number) => `${year}-${String(index + 1).padStart(2, '0')}`;

/**
 * Rows of the editor: Przychód, Marża, each active group (in its order), an
 * archived group only when it has a plan this year, „Bez grupy”, Zysk netto.
 */
export function planRows(budget: CompanyBudgetWorkspaceDto | null): PlanRow[] {
  const groups = budget?.groups ?? [];
  const planned = new Set((budget?.months ?? []).flatMap((month) => month.budget.groups.map((group) => group.groupId)));
  const visible = groups
    .filter((group) => group.isActive || planned.has(group.id))
    .sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'pl'));
  return [
    { key: 'revenue', kind: 'revenue', signed: false },
    { key: 'margin', kind: 'margin', signed: true },
    ...visible.map((group): PlanRow => ({ key: groupRowKey(group.id), kind: 'group', group, signed: false })),
    { key: 'ungrouped', kind: 'ungrouped', signed: false },
    { key: 'profit', kind: 'profit', signed: true },
  ];
}

/** The saved plan as editor values; a month without a plan is empty, never 0. */
export function buildPlanGrid(budget: CompanyBudgetWorkspaceDto | null, year: number, rows: PlanRow[]): PlanGrid {
  const grid: PlanGrid = Object.fromEntries(rows.map((row) => [row.key, emptyRow()]));
  for (let index = 0; index < 12; index += 1) {
    const month = budget?.months.find((item) => item.month === monthKey(year, index));
    const plan = month?.budget;
    if (!plan?.hasPlan) continue;
    grid.revenue[index] = plan.revenue > 0 ? plan.revenue : null;
    grid.margin[index] = plan.margin;
    grid.profit[index] = plan.netProfit;
    grid.ungrouped[index] = plan.ungrouped > 0 ? plan.ungrouped : null;
    plan.groups.forEach((group) => {
      const key = groupRowKey(group.groupId);
      if (grid[key]) grid[key][index] = group.amount;
    });
  }
  return grid;
}

export function rowTotal(values: PlanValues): number | null {
  const known = values.filter((value): value is number => value != null);
  return known.length ? roundMoney(known.reduce((sum, value) => sum + value, 0)) : null;
}

/** The first filled month copied into every month of the row. */
export function copyFirstMonth(values: PlanValues): PlanValues {
  const first = values.find((value) => value != null);
  return first == null ? values : values.map(() => first);
}

/** A yearly amount split evenly over 12 months in whole grosze; the rounding rest goes to December. */
export function spreadEvenly(annual: number): PlanValues {
  const monthly = roundMoney(annual / 12);
  const values: PlanValues = Array.from({ length: 12 }, () => monthly);
  values[11] = roundMoney(annual - monthly * 11);
  return values;
}

export interface PlanPreviewMonth {
  fixedCosts: number;
  ebitda: number | null;
  /** EBITDA − CIT year to date (backend calculatedPlanProfits). */
  profitCalculated: number | null;
}

/** Derived rows of the editor: fixed costs = groups + „Bez grupy”, EBITDA = margin − fixed costs. */
export function planPreview(grid: PlanGrid, rows: PlanRow[]): PlanPreviewMonth[] {
  const costRows = rows.filter((row) => row.kind === 'group' || row.kind === 'ungrouped');
  let cumulative = 0;
  let charged = 0;
  return Array.from({ length: 12 }, (_, index) => {
    const fixedCosts = roundMoney(costRows.reduce((sum, row) => sum + (grid[row.key]?.[index] ?? 0), 0));
    const margin = grid.margin?.[index] ?? null;
    const ebitda = margin == null ? null : roundMoney(margin - fixedCosts);
    let profitCalculated: number | null = null;
    if (ebitda != null) {
      cumulative += ebitda;
      const taxToDate = cumulative > 0 ? roundMoney(cumulative * PLAN_INCOME_TAX_RATE) : 0;
      const tax = roundMoney(taxToDate - charged) + 0;
      charged = roundMoney(charged + tax);
      profitCalculated = roundMoney(ebitda - tax);
    }
    return { fixedCosts, ebitda, profitCalculated };
  });
}

/** Cells outside the allowed range (`row.key:index`); save is blocked while any exists. */
export function invalidCells(grid: PlanGrid, rows: PlanRow[]): Set<string> {
  const invalid = new Set<string>();
  rows.forEach((row) => (grid[row.key] ?? []).forEach((value, index) => {
    if (value == null) return;
    const min = row.signed ? -MAX_PLAN_AMOUNT : 0;
    if (!Number.isFinite(value) || value < min || value > MAX_PLAN_AMOUNT) invalid.add(`${row.key}:${index}`);
  }));
  return invalid;
}

/**
 * The months whose values changed, each with its whole plan: revenue (empty =
 * 0), margin and net profit (empty = no plan), every group with a value (an
 * emptied group drops out of the month) and „Bez grupy” (empty = 0).
 */
export function changedMonthsPayload(year: number, rows: PlanRow[], initial: PlanGrid, current: PlanGrid): CompanyBudgetYearMonthInput[] {
  const result: CompanyBudgetYearMonthInput[] = [];
  for (let index = 0; index < 12; index += 1) {
    const changed = rows.some((row) => (initial[row.key]?.[index] ?? null) !== (current[row.key]?.[index] ?? null));
    if (!changed) continue;
    const value = (key: string) => current[key]?.[index] ?? null;
    result.push({
      month: monthKey(year, index),
      revenueTarget: value('revenue') ?? 0,
      marginTarget: value('margin'),
      netProfitTarget: value('profit'),
      groupCostTargets: rows
        .filter((row) => row.kind === 'group' && value(row.key) != null)
        .map((row) => ({ groupId: row.group!.id, amount: value(row.key) as number })),
      ungroupedCostTarget: value('ungrouped') ?? 0,
    });
  }
  return result;
}
